import Constants from '../Constants.js';
import OidcClientReducer from './OidcClientReducer.js';
const {
  getStatus,
  getError
} = OidcClientReducer;

/**
 * @class OidcClient An interface for a single OIDC Client
 * @property {string} projectId The ID of the project that contains your OIDC Client
 * @property {string} id The ID of the OIDC Client this class represents.
 * If not provided, `process.env.projectId` will be used. 
 */
class OidcClient {
  constructor(projectId, oidcClientId) {
    this.projectId = projectId || process?.env?.projectId;
    this.id = oidcClientId;
  }

  /**
   * @method createAction A creator for actions
   * @memberof OidcClient
   * @param {string} method The name of the method being created (e.g. 'update')
   * @param {string} status The status of the action (e.g. 'updating')
   * @param {function} func The async function that executes the action
   */
  createAction(method, status, func) {
    return (dispatch, getState, opts) => {
      const projectId = this.projectId || opts?.api?.projectId;
      dispatch({
        type: Constants.OIDC_CLIENT_STATUS,
        projectId,
        id: this.id,
        status
      });
      return func(dispatch, getState, opts, projectId).catch(error => {
        dispatch({
          type: Constants.OIDC_CLIENT_STATUS,
          projectId,
          oidcClientId: this.id,
          status: `${method}Error`,
          error
        });
        return Promise.reject(error);
      });
    };
  }

  /**
   * @method get An action to get the OidcClient
   * @memberof OidcClient
   * @param {object} opts Options for the `oidcClient.get( opts )` api call
   * @returns {promise}
   */
  get(opts) {
    return this.createAction('load', 'loading', async (dispatch, getState, {
      api
    }, projectId) => {
      const getOidcClientRes = await api.oidcClient(this.id).get(opts);
      dispatch({
        type: Constants.OIDC_CLIENT_GET,
        projectId,
        oidcClient: getOidcClientRes
      });
      return getOidcClientRes;
    });
  }

  /**
   * @method update An action to update the OidcClient
   * @memberof OidcClient
   * @param {object} data The patch object for the OidcClient
   * @returns {promise}
   */
  update(data) {
    return this.createAction('update', 'updating', async (dispatch, getState, {
      api
    }, projectId) => {
      const patchOidcClientRes = await api.oidcClient(this.id).update(data);
      dispatch({
        type: Constants.OIDC_CLIENT_UPDATE,
        projectId,
        oidcClient: patchOidcClientRes
      });
      return patchOidcClientRes;
    });
  }

  /**
   * @method delete An action to delete the OidcClient
   * @memberof OidcClient
   * @returns {promise}
   */
  delete() {
    return this.createAction('delete', 'deleting', async (dispatch, getState, {
      api
    }, projectId) => {
      const deleteOidcClientRes = await api.oidcClient(this.id).delete();
      dispatch({
        type: Constants.OIDC_CLIENT_REMOVE,
        projectId,
        oidcClientId: this.id
      });
      return deleteOidcClientRes;
    });
  }

  /**
   * @method loginUrl Get the OidcClient login URL
   * @memberof OidcClient
   * @param {object} oidcClient Contains the client ID, alias, and project ID of the OIDC client
   * @param {string} oidcClient.projectId The projectId to log into
   * @param {string} oidcClient.id Contains the id of oidc client
   * @param {string} oidcClient.alias Contains the alias of oidc client
   * @param {string} successUrl The redirect URL to be called with the API token as a query param when OIDC authentication is successful.
   * Call `api.setToken( token )` with the token param on the success URL so that the `api` object can make API calls on behalf of the identified user.
   * @param {string} failureUrl The redirect URL to be called when OIDC authentication fails
   * @param {object} urlOpts Contains info about how to handle the login URL
   * @param {string} urlOpts.prompt The prompt form to use (none, login, consent, select_account)
   * @param {string} urlOpts.display The display form to use (page, popup, touch, wrap)
   * @returns {promise} 
   * @see{@link https://leverege.atlassian.net/wiki/spaces/PLAT/pages/1341292548/Add+OIDC+Clients+to+your+App+for+Federated+Login}
   */
  loginUrl(oidcClient, successUrl, failureUrl, urlOpts) {
    return this.createAction('load', 'loading', async (dispatch, getState, {
      api
    }, projectId) => {
      const oidcLoginUrlRes = await api.oidcLoginUrl(oidcClient, successUrl, failureUrl, urlOpts);
      dispatch({
        type: Constants.OIDC_CLIENT_LOGIN_URL,
        projectId,
        oidcClientId: this.id,
        oidcClientLoginUrl: oidcLoginUrlRes
      });
      return oidcLoginUrlRes;
    });
  }

  /**
   * @method getStatus Get the status of the OidcClient actions from Redux state
   * @memberof OidcClient
   * @returns {promise}
   */
  getStatus(state) {
    return getStatus(state, this.projectId);
  }

  /**
   * @method getError Get the current error for the OidcClient actions from Redux state
   * @memberof OidcClient
   * @returns {promise}
   */
  getError(state) {
    return getError(state, this.projectId);
  }
}
export default OidcClient;